import type { KeyboardEvent, ReactNode } from 'react';
import { ArrowUp, Square, X } from 'lucide-react';
import { FOCUS_RING } from '@/components/console-ui';
import { cn } from 'helpers/cn';
import type { ChatWorkingPhase } from './ChatMessages';
import type { ChatMeter } from './MeterTrack';
import { GlowHalo } from './LogoBars';
import { MeterTrack } from './MeterTrack';

type ChatInputBoxProps = {
  inputId: string;
  /** The textarea's accessible name, read by a screen reader only. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (ev: KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
  /** Nothing can be typed yet (e.g. a notice to accept first). */
  disabled?: boolean;
  /** A turn is running: the box is read-only, keeps focus so Esc can stop it, and Stop replaces Send. */
  busy: boolean;
  /** The glow's speed while a turn runs; no glow at rest. */
  phase: ChatWorkingPhase | null;
  canSend: boolean;
  onSend: () => void;
  onStop: () => void;
  /** The line beside the buttons: the working words, an estimate, or why nothing can be sent. */
  status: string;
  /** A refusal (e.g. "Out of credits"), in red, describing the textarea and Send. */
  statusAlert?: { id: string; text: string };
  /** Removable chips above the text: what rides along with the message. */
  chips?: ReactNode;
  /** Icon buttons left of the status line. */
  tools?: ReactNode;
  meter?: ChatMeter | null;
};

/**
 * The message box (handoff 4.7): Enter sends, Shift+Enter adds a line, Esc stops a running turn
 * (the caller's onKeyDown). The box takes the working glow while a request runs. The credit
 * meter, when there is one, sits beneath it.
 */
export const ChatInputBox = (props: ChatInputBoxProps) => {
  const { inputId, label, value, onChange, onKeyDown, placeholder, disabled, busy, phase, canSend } = props;
  const { onSend, onStop, status, statusAlert, chips, tools, meter } = props;
  const describedBy = statusAlert && !busy ? statusAlert.id : undefined;
  const glow = phase === 'writing' ? 'is-working' : phase === 'thinking' ? 'is-thinking' : undefined;

  return (
    <div className="shrink-0 border-t border-line p-3">
      <div className={cn('ai-glow-box relative isolate rounded-card bg-panel', glow && 'ai-glow', glow)}>
        <GlowHalo />
        {chips}
        <label htmlFor={inputId} className="sr-only">
          {label}
        </label>
        <textarea
          id={inputId}
          rows={2}
          value={value}
          // Read-only rather than disabled while working, so it keeps focus and Esc can stop the turn.
          readOnly={busy}
          aria-disabled={busy || undefined}
          aria-describedby={describedBy}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(ev) => onChange(ev.target.value)}
          onKeyDown={onKeyDown}
          className="block max-h-40 min-h-[52px] w-full resize-none bg-transparent px-3 pt-2.5 pb-1 text-meta leading-normal text-foreground outline-none placeholder:text-dim read-only:cursor-default disabled:cursor-not-allowed"
        />
        <div className="flex items-center gap-1 px-2 pb-2">
          {tools}
          {describedBy && statusAlert ? (
            <span id={statusAlert.id} className="ml-1 min-w-0 flex-1 truncate text-nano font-medium text-danger">
              {statusAlert.text}
            </span>
          ) : (
            <span className="ml-1 min-w-0 flex-1 truncate text-nano text-dim">{status}</span>
          )}
          {busy ? (
            <button
              type="button"
              onClick={onStop}
              aria-keyshortcuts="Escape"
              className={cn(
                'inline-flex h-[30px] items-center gap-1.5 rounded-lg border border-line px-2.5 text-micro font-medium text-foreground hover:bg-field',
                FOCUS_RING,
              )}
            >
              <Square aria-hidden="true" className="size-3 fill-current" />
              Stop
            </button>
          ) : (
            <button
              type="button"
              aria-label="Send"
              aria-disabled={!canSend}
              aria-describedby={describedBy}
              onClick={onSend}
              className={cn(
                'inline-flex size-[30px] items-center justify-center rounded-lg bg-foreground text-background hover:opacity-90',
                'aria-disabled:cursor-not-allowed aria-disabled:opacity-40',
                FOCUS_RING,
              )}
            >
              <ArrowUp aria-hidden="true" className="size-4" />
            </button>
          )}
        </div>
      </div>
      {meter && (
        <div className="mt-2.5 flex items-center gap-3">
          <MeterTrack meter={meter} className="h-[3px] flex-1" />
          <span
            className={cn(
              'shrink-0 font-mono text-nano text-dim',
              meter.level === 'low' && 'text-warn',
              meter.level === 'empty' && 'text-danger',
            )}
          >
            {meter.line}
          </span>
        </div>
      )}
    </div>
  );
};

type ChatChipProps = {
  children: ReactNode;
  /** The remove button's accessible name. */
  removeLabel: string;
  onRemove: () => void;
  /** Code-like content (an element's name) is set in mono. */
  mono?: boolean;
};

/** A removable chip above the message text, in the row `ChatChips` lays out. */
export const ChatChip = ({ children, removeLabel, onRemove, mono }: ChatChipProps) => (
  <span
    className={cn(
      'inline-flex max-w-full items-center gap-1 rounded-md bg-accent/10 py-0.5 pr-1 pl-1.5 text-nano text-accent',
      mono ? 'font-mono' : 'font-medium',
    )}
  >
    <span className="truncate">{children}</span>
    <button
      type="button"
      aria-label={removeLabel}
      onClick={onRemove}
      className={cn('rounded-sm p-0.5 hover:bg-accent/15', FOCUS_RING)}
    >
      <X aria-hidden="true" className="size-3" />
    </button>
  </span>
);

export const ChatChips = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-wrap gap-1.5 px-3 pt-2.5">{children}</div>
);
